/* The Overview's line icons: one stroke weight, one grid, `currentColor`, and always decorative.
 *
 * **Hand-drawn SVG rather than an icon package**, for `navIcons`' reason one directory over: a package
 * is a dependency whose licence and air-gap story need checking, and this is a dozen paths. They sit
 * on a 24-unit grid at a 1.6 stroke with round caps and joins, so every icon on the page has the same
 * weight — the reference's line icons read as one family because they are one family.
 *
 * **Never the only carrier of meaning.** Every icon here sits beside a word that says the same thing,
 * so they are `aria-hidden` and a reader who cannot see them loses nothing. That is also why the AI
 * icon is a proposed-rules list rather than sparkles: the design direction rules out "AI sparkle"
 * motifs, and an icon that has to be decoded is not doing an icon's job.
 */

import type { ReactElement, ReactNode } from 'react';

function Icon({
  children,
  className,
}: {
  readonly children: ReactNode;
  readonly className?: string | undefined;
}): ReactElement {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

interface IconProps {
  readonly className?: string | undefined;
}

/** Quality score: a gauge — a reading on a scale. */
export const GaugeIcon = ({ className }: IconProps): ReactElement => (
  <Icon className={className}>
    <path d="M4.5 16.5a8 8 0 1 1 15 0" />
    <path d="M12 12.5l3.5-3.5" />
    <circle cx="12" cy="13" r="1.2" />
  </Icon>
);

/** Datasets measured: stacked tables. */
export const LayersIcon = ({ className }: IconProps): ReactElement => (
  <Icon className={className}>
    <path d="M12 4l8 4-8 4-8-4 8-4z" />
    <path d="M4 12l8 4 8-4" />
    <path d="M4 16l8 4 8-4" />
  </Icon>
);

/** An incident or a warning: the triangle. */
export const AlertIcon = ({ className }: IconProps): ReactElement => (
  <Icon className={className}>
    <path d="M10.3 4.6L3.4 17a2 2 0 0 0 1.7 3h13.8a2 2 0 0 0 1.7-3L13.7 4.6a2 2 0 0 0-3.4 0z" />
    <path d="M12 9.5v4" />
    <path d="M12 17h.01" />
  </Icon>
);

/** Something critical: the octagon, a different shape from a warning so severity survives greyscale. */
export const OctagonIcon = ({ className }: IconProps): ReactElement => (
  <Icon className={className}>
    <path d="M8.5 3.5h7l5 5v7l-5 5h-7l-5-5v-7z" />
    <path d="M12 8v4.5" />
    <path d="M12 16h.01" />
  </Icon>
);

/** Rule failures: a circled cross. */
export const FailIcon = ({ className }: IconProps): ReactElement => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="8" />
    <path d="M9.2 9.2l5.6 5.6" />
    <path d="M14.8 9.2l-5.6 5.6" />
  </Icon>
);

/** An anomaly: a pulse line. */
export const PulseIcon = ({ className }: IconProps): ReactElement => (
  <Icon className={className}>
    <path d="M3 12h4l2.5-6 4 12 2.5-6H21" />
  </Icon>
);

/** Classifications waiting for review: a shield with a mark. */
export const ShieldIcon = ({ className }: IconProps): ReactElement => (
  <Icon className={className}>
    <path d="M12 3.5l7 3v5.2c0 4.3-3 7.6-7 8.8-4-1.2-7-4.5-7-8.8V6.5z" />
    <path d="M9.2 12l2 2 3.6-3.8" />
  </Icon>
);

/** Approvals waiting: a stamp. */
export const StampIcon = ({ className }: IconProps): ReactElement => (
  <Icon className={className}>
    <path d="M9.5 12.5V9a2.5 2.5 0 1 1 5 0v3.5" />
    <path d="M5 16.5h14v3H5z" />
    <path d="M7 12.5h10l1 4H6z" />
  </Icon>
);

/** Connect a source: a plug. */
export const PlugIcon = ({ className }: IconProps): ReactElement => (
  <Icon className={className}>
    <path d="M9 3.5v4" />
    <path d="M15 3.5v4" />
    <path d="M6.5 7.5h11v3a5.5 5.5 0 0 1-11 0z" />
    <path d="M12 16v4.5" />
  </Icon>
);

/** Author a rule: a checklist. */
export const RuleIcon = ({ className }: IconProps): ReactElement => (
  <Icon className={className}>
    <path d="M10 6.5h9.5" />
    <path d="M10 12h9.5" />
    <path d="M10 17.5h9.5" />
    <path d="M4.5 6.5l1.2 1.2L7.8 5.5" />
    <path d="M4.5 12l1.2 1.2 2.1-2.2" />
    <path d="M4.5 17.5l1.2 1.2 2.1-2.2" />
  </Icon>
);

/** Rule suggestions: a list being added to — proposed, not yet accepted. */
export const ProposeIcon = ({ className }: IconProps): ReactElement => (
  <Icon className={className}>
    <path d="M4 6.5h11" />
    <path d="M4 12h7" />
    <path d="M4 17.5h7" />
    <path d="M17.5 13.5v7" />
    <path d="M14 17h7" />
  </Icon>
);

/** Draft a contract: a document with a signature line. */
export const ContractIcon = ({ className }: IconProps): ReactElement => (
  <Icon className={className}>
    <path d="M14 3.5H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8.5z" />
    <path d="M14 3.5v5h5" />
    <path d="M8.5 16.5c1-1.4 2-1.4 2.6 0s1.6 1.4 2.4 0 1.6-.8 2 0" />
  </Icon>
);

/** A timer or sweep acting, rather than a person. */
export const CycleIcon = ({ className }: IconProps): ReactElement => (
  <Icon className={className}>
    <path d="M19 8.5A7.5 7.5 0 0 0 5.6 7.4" />
    <path d="M5 4.5v3h3" />
    <path d="M5 15.5a7.5 7.5 0 0 0 13.4 1.1" />
    <path d="M19 19.5v-3h-3" />
  </Icon>
);

/** Freshness: a clock. */
export const ClockIcon = ({ className }: IconProps): ReactElement => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="8" />
    <path d="M12 7.5V12l3 2" />
  </Icon>
);

/** Onward: the arrow on an action link, which nudges right on hover. */
export const ArrowIcon = ({ className }: IconProps): ReactElement => (
  <Icon className={className}>
    <path d="M5 12h14" />
    <path d="M13.5 6.5L19 12l-5.5 5.5" />
  </Icon>
);
